/**
 * MapLayoutContext for managing layout in the Needs Map
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Node, Edge } from '@xyflow/react';
import { TopicNode } from '@/types/nodeTypes';
import { TopicEdge } from '@/types/edgeTypes';
import { Phase } from '@/types/phaseTypes';
import { applyTimelineLayout, applyTimelineLayoutToTopicNodes } from '@/components/map/layouts/TimelineLayout';
import { applyRelationLayout, applyRelationLayoutToTopicNodes } from '@/components/map/layouts/RelationLayout';
import { 
  applyPhaseLayout, 
  applyPhaseLayoutToTopicNodes,
  generatePhaseLayoutElements 
} from '@/components/map/layouts/PhaseLayout';
import { generateContentTimelineLayout } from '@/components/map/layouts/ContentTimelineLayout';

/**
 * Layout types
 */
export type LayoutType = 'timeline' | 'relation' | 'phase' | 'content-timeline';

/**
 * MapLayoutContext type
 */
interface MapLayoutContextType {
  // State
  layoutType: LayoutType;
  
  // Operations
  changeLayout: (type: LayoutType) => void;
  applyLayout: (
    nodes: Node[],
    edges: Edge[],
    phases: Phase[],
    type?: LayoutType,
    width?: number,
    height?: number,
    defaultNodeWidth?: number,
    defaultNodeHeight?: number
  ) => Node[];
  applyLayoutToTopicNodes: (
    nodes: TopicNode[],
    edges: TopicEdge[],
    phases: Phase[],
    type?: LayoutType,
    width?: number,
    height?: number,
    defaultNodeWidth?: number,
    defaultNodeHeight?: number
  ) => TopicNode[];
  generateLayoutElements: (
    nodes: TopicNode[],
    edges: TopicEdge[],
    phases: Phase[],
    type?: LayoutType,
    width?: number,
    height?: number
  ) => { nodes: Node[], edges: Edge[] };
}

/**
 * Create MapLayoutContext
 */
const MapLayoutContext = createContext<MapLayoutContextType | undefined>(undefined);

/**
 * MapLayoutProvider props
 */
interface MapLayoutProviderProps {
  children: React.ReactNode;
  initialLayoutType?: LayoutType;
}

/**
 * MapLayoutProvider component
 */
export const MapLayoutProvider: React.FC<MapLayoutProviderProps> = ({ 
  children,
  initialLayoutType = 'phase' // デフォルトレイアウトをフェーズ別に変更
}) => {
  const [layoutType, setLayoutType] = useState<LayoutType>(initialLayoutType);
  
  /**
   * Apply layout to nodes
   */
  const applyLayout = useCallback(
    (
      nodes: Node[],
      edges: Edge[],
      phases: Phase[],
      type: LayoutType = layoutType,
      width: number = window.innerWidth,
      height: number = window.innerHeight,
      defaultNodeWidth: number = 250,
      defaultNodeHeight: number = 150
    ): Node[] => {
      console.log(`Applying ${type} layout to ${nodes.length} nodes`);
      
      switch (type) {
        case 'timeline':
          return applyTimelineLayout(nodes, edges, width, height, defaultNodeWidth, defaultNodeHeight);
        case 'relation':
          return applyRelationLayout(nodes, edges, width, height, 100, defaultNodeWidth, defaultNodeHeight);
        case 'phase':
          // For phase layout, include phase nodes
          return applyPhaseLayout(nodes, edges, phases, width, height, defaultNodeWidth, defaultNodeHeight, 180, 60, true);
        // Content Timeline は applyLayout では処理せず generateLayoutElements で処理する
        default:
          return nodes;
      }
    },
    [layoutType]
  );
  
  /**
   * Apply layout to topic nodes
   */
  const applyLayoutToTopicNodes = useCallback(
    (
      nodes: TopicNode[],
      edges: TopicEdge[],
      phases: Phase[],
      type: LayoutType = layoutType,
      width: number = window.innerWidth,
      height: number = window.innerHeight,
      defaultNodeWidth: number = 250,
      defaultNodeHeight: number = 150
    ): TopicNode[] => {
      console.log(`Applying ${type} layout to ${nodes.length} topic nodes`);
      
      switch (type) {
        case 'timeline':
          return applyTimelineLayoutToTopicNodes(nodes, edges, width, height, defaultNodeWidth, defaultNodeHeight);
        case 'relation':
          return applyRelationLayoutToTopicNodes(nodes, edges, width, height, defaultNodeWidth, defaultNodeHeight);
        case 'phase':
          // For phase layout, now using new implementation with phase nodes
          return applyPhaseLayoutToTopicNodes(nodes, edges, phases, width, height, defaultNodeWidth, defaultNodeHeight, true);
        // Content Timeline はトピックノードの位置を変更しないので、そのまま返す
        case 'content-timeline':
          return nodes;
        default:
          return nodes;
      }
    },
    [layoutType]
  );
  
  /**
   * Generate complete set of nodes and edges for a layout
   * This allows us to add special nodes and edges for certain layouts (like phase nodes)
   */
  const generateLayoutElements = useCallback(
    (
      nodes: TopicNode[],
      edges: TopicEdge[],
      phases: Phase[],
      type: LayoutType = layoutType,
      width: number = window.innerWidth,
      height: number = window.innerHeight
    ): { nodes: Node[], edges: Edge[] } => {
      console.log(`Generating elements for ${type} layout`);
      
      // Default conversion (used for timeline and relation layouts)
      const defaultConversion = () => {
        // Position the nodes according to the layout
        const positionedNodes = applyLayoutToTopicNodes(
          nodes, edges, phases, type, width, height
        );
        
        // Convert to ReactFlow nodes and edges
        const rfNodes: Node[] = positionedNodes.map(node => ({
          id: node.id,
          type: 'topicNode',
          position: node.position || { x: 0, y: 0 },
          data: {
            ...node,
            width: 250,
            height: 150
          }
        }));
        
        const rfEdges: Edge[] = edges.map(edge => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          type: 'customEdge',
          data: { ...edge }
        }));
        
        return { nodes: rfNodes, edges: rfEdges };
      };
      
      switch (type) {
        case 'phase':
          // Use special generator for phase layout
          return generatePhaseLayoutElements(nodes, edges, phases, width, height);
          
        case 'content-timeline':
          // Use special generator for content timeline layout
          return generateContentTimelineLayout(nodes, edges, phases, width, height);
          
        case 'timeline':
        case 'relation':
        default:
          return defaultConversion();
      }
    },
    [layoutType, applyLayoutToTopicNodes]
  );
  
  /**
   * Change layout type
   */
  const changeLayout = useCallback((type: LayoutType) => {
    console.log('Changing layout to:', type);
    setLayoutType(type);
  }, []);
  
  // Log when layout type changes
  useEffect(() => {
    console.log('Layout type set to:', layoutType);
  }, [layoutType]);
  
  const value = {
    layoutType,
    changeLayout,
    applyLayout,
    applyLayoutToTopicNodes,
    generateLayoutElements
  };
  
  return (
    <MapLayoutContext.Provider value={value}>
      {children}
    </MapLayoutContext.Provider>
  );
};

/**
 * useMapLayout hook
 */
export const useMapLayout = (): MapLayoutContextType => {
  const context = useContext(MapLayoutContext);
  
  if (context === undefined) {
    throw new Error('useMapLayout must be used within a MapLayoutProvider');
  }
  
  return context;
};